import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackFAQInteraction } from '../utils/analytics';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

const faqs: FAQItem[] = [
  {
    question: "Who can leave a review?",
    answer: "Anyone who’s rented a place in the UK, whether it’s a student flat, a family home, or a short-term let. If you’ve lived there, your voice matters."
  },
  {
    question: "Can I post a review anonymously?",
    answer: `Absolutely. You can use your full name, just your first name, or tick the box to stay completely anonymous, whatever feels most comfortable for you. Your contact details stay private.`
  },
  {
    question: "How are reviews checked?",
    answer: `When you post a review, our system gives it a quick scan to block anything racist, discriminatory, or overly aggressive before it goes live. If something's flagged, you'll be asked to tweak it. We also keep an eye on things ourselves and can remove anything we feel crosses the line - even if it slipped through the first check.`
  },
  {
    question: "What if I don't know the landlord's name?",
    answer: `No worries. Just skip that bit, you can still review the property and/or the letting agent.`
  },
  {
    question: "Why do you take my email and phone number?",
    answer: (
      <>
        It's just so we can check your review is genuine and get in touch if we need to confirm something. We'll never publish your details or hand them out without your permission. You can see more about how we use your data in our{' '}
        <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800 underline">
          Privacy Policy
        </Link>
        .
      </>
    )
  },
  {
    question: "Will you tell the landlord/agent about my review?",
    answer: `Yes, we might let them know a review's been posted so they can see the feedback. Don't worry, we won't share your contact details.`
  },
  {
    question: "Can I edit or delete my review later?",
    answer: (
      <>
        Yes, just drop us an email at{' '}
        <a href="mailto:info@rentreview.io" className="text-blue-600 hover:text-blue-800 underline">
          info@rentreview.io
        </a>
        {' '}from the same address you used when posting.
      </>
    )
  },
  {
    question: "Can I leave a review if my tenancy was years ago?",
    answer: "Yes! If you've rented the place before, even if it was a while back, your experience is still valuable. Just make sure your review is honest and based on what it was like when you lived there."
  },
  {
    question: "What if I've never rented but dealt with an agent for a viewing or application?",
    answer: "At the moment, reviews are for people who've actually rented the property or had a tenancy with that landlord/agent. If you've only had a viewing or applied, you can't leave a full review, but we may add a space for that kind of feedback in future."
  },
  {
    question: "Is it free to use?",
    answer: "Yes! Totally free. No subscriptions, no hidden fees, just a place for renters to share their experiences!"
  },

  ];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleFAQClick = (index: number, question: string) => {
    const isOpening = openIndex !== index;
    setOpenIndex(isOpening ? index : null);
    trackFAQInteraction(question, isOpening ? 'open' : 'close');
  };

  return (
    <section className="pt-4 pb-16">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-6xl">💬</span>
            <h2 className="text-5xl font-bold">FAQs</h2>
          </div>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              <button
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 gap-4"
                onClick={() => handleFAQClick(index, faq.question)}
              >
                <span className="font-bold text-lg text-gray-900 flex items-center gap-2">
                  <img 
                    src="https://rentreview.io/wp-content/uploads/2025/08/Question-Mark-Better.png" 
                    alt="Question mark" 
                    className="w-5 h-5"
                  />
                  {faq.question}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-500 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-6 py-4 bg-gray-50">
                  <div className="text-gray-600">
                    {typeof faq.answer === 'string' ? (
                      <p>{faq.answer}</p>
                    ) : (
                      <p>{faq.answer}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}